import { CheckCircleTwoTone, CloseCircleTwoTone, SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, DatePicker, Form, Input, Modal, Row, Select } from 'antd';
import Search from 'antd/lib/input/Search';
import moment from 'moment';
import React, { ReactElement, useState } from 'react';
import { useDispatch } from 'react-redux';
import { GUTTOR_SPACE, VALIDATE_MESSAGES } from '../../../shared/constants/commonConstants';
import { reloadHIUList, saveRequestConcent, searchHealthId } from '../../../store/actions/abdmHiuAction';
import { getDateWithTextMonth, getDateWithTime } from '../../../utils/dateTimeUtils';
import { centeredNotification } from '../../../utils/utility';
import { HEALTH_INFO_TYPE, isValidationEnabled, PURPOSE_OF_REQUEST } from '../abdmConstants';
import { dateDisabledBeforeToday, dateDisabledSameOrBefore } from '../abdmUtils';

const NewConcentModal = (props: any): ReactElement => {
  const { visible, onClose } = props || {};
  const [concentForm] = Form.useForm();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [isAbhaVerified, setIsAbhaVerified] = useState(false);
  const onSubmit = (): any => {
    concentForm.validateFields().then((): any => {
      setIsLoading(true);
      dispatch(saveRequestConcent(concentForm.getFieldsValue(), onSaveSuccess, onSaveErr));
    });
  };

  const onSaveSuccess = (): any => {
    setIsLoading(false);
    centeredNotification('Consent raised successfully!', 'success');
    dispatch(reloadHIUList());
    onClose();
  };
  const onSaveErr = (err: string): any => {
    setIsLoading(false);
    centeredNotification(err);
  };

  const onSearch = (text: any): any => {
    setIsSearching(true);
    dispatch(searchHealthId(text, callBack));
  };

  const callBack = ({ isSuccess, id, healthIdNumber, name }: any = {}): any => {
    setIsSearching(false);
    if (isSuccess) {
      setErrMsg('');
      concentForm.setFieldsValue({
        patientIdentifier: id,
        name,
      });
      setIsAbhaVerified(true);
    } else {
      setErrMsg('Invalid patient id');
    }
    concentForm.validateFields(['patientIdentifier']);
  };

  const searchValidator = ({ getFieldValue }: any): any => ({
    validator(): any {
      if (!getFieldValue('patientIdentifier') || !errMsg) {
        return Promise.resolve();
      }
      return Promise.reject('Incorrect patient identifier!');
    },
  });

  const onSearchChangeHandler = (): any => {
    setIsAbhaVerified(false);
  };

  return (
    <Modal
      visible={visible}
      title={(<strong className="text-primary">Consent Request Form</strong>)}
      footer={null}
      onCancel={onClose}
      width={900}
    >
      <Form
        form={concentForm}
        labelCol={{ span: 8, style: { marginTop: 5, marginRight: 10 } }}
        wrapperCol={{ span: 16 }}
        colon={false}
        onFinish={onSubmit}
        validateMessages={VALIDATE_MESSAGES}
        className="abdmForm"
      >
        <Row gutter={GUTTOR_SPACE}>
          <Col md={12}>
            <Form.Item label="Patient's ABHA Address" name="patientIdentifier" rules={[{ required: true }, searchValidator]}>
              <Search
                suffix={ isValidationEnabled ? isAbhaVerified ? <CheckCircleTwoTone twoToneColor="#2abf86" /> :
                <CloseCircleTwoTone twoToneColor="#e15554" /> : <></>}
                placeholder="@stw"
                onChange={onSearchChangeHandler}
                onSearch={onSearch}
                enterButton={<Button type="text" className="search-icon" loading={isSearching} icon={<SearchOutlined />} />} />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item label="Health Info from" name="healthInfoFrom" rules={[{ required: true }]}>
              <DatePicker
                disabledDate={dateDisabledSameOrBefore}
                placeholder={getDateWithTextMonth(moment())}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={GUTTOR_SPACE} className="mt-sm">
          <Col md={12}>
            <Form.Item label="Purpose of Request" name="requestPurpose" rules={[{ required: true }]}>
              <Select placeholder="Select purpose of request" options={PURPOSE_OF_REQUEST} />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item label="Health Info To" name="healthInfoTo" rules={[{ required: true }]}>
              <DatePicker
                disabledDate={dateDisabledSameOrBefore}
                placeholder={getDateWithTextMonth(moment())}
              />
            </Form.Item>
          </Col>
          <Col md={12}>
            <Form.Item label="Patient Name" name="name" hidden={true}>
              <Input placeholder="Enter Patient Name" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={GUTTOR_SPACE} className="mt-md mb-md">
          <Col md={12}>
            <Form.Item label="Health Info Type" name="healthInfoType" rules={[{ required: true }]}>
              <Checkbox.Group>
                <Row>
                  {
                    HEALTH_INFO_TYPE.map(({ label, value }: any): any => (
                      <Col md={24} key={value}>
                        <Checkbox value={value}>{label}</Checkbox>
                      </Col>
                    ))
                  }
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={GUTTOR_SPACE}>
          <Col md={12}>
            <Form.Item label="Consent Expiry" name="consentExpiry" rules={[{ required: true }]}>
              <DatePicker
                showTime={true}
                disabledDate={dateDisabledBeforeToday}
                placeholder={getDateWithTime(moment())}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={GUTTOR_SPACE} justify="center" className="mt-lg">
          <Col md={4}>
            <Button type="primary" htmlType="submit" loading={isLoading}
            disabled={!isAbhaVerified && isValidationEnabled}>Request Consent</Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default NewConcentModal;
